/**
 * Typed client for the development API (`/api/development`): multi-track enrollments, the eight
 * WorkSim stages, the skill matrix, ranked recommendations, the career readiness review and the
 * dashboard overview.
 *
 * Response types are declared here rather than in `api/types.ts` so the progression views own
 * their own contract. Shapes mirror `packages/server/src/services/development.ts`; anything the
 * server may omit is optional here so a missing field never crashes a page.
 */
import { request } from './client';
import type {
  CareerReadinessReview,
  CertificationReadiness,
  Enrollment,
  Recommendation,
  SkillMasteryStatus,
  SkillMatrix,
  SkillMatrixRow,
  StageId,
  StageStatus,
} from '@worksim/engine';

export type {
  CareerReadinessReview,
  CertificationReadiness,
  Enrollment,
  Recommendation,
  SkillMasteryStatus,
  SkillMatrix,
  SkillMatrixRow,
  StageId,
  StageStatus,
};

/** `GET /development/enrollments` and every enrollment mutation. */
export interface EnrollmentsResponse {
  enrollments: Enrollment[];
}

export interface EnrollmentMutation {
  enrollment: Enrollment;
  enrollments: Enrollment[];
}

/** `GET /development/stages`. */
export interface StagesResponse {
  stages: StageStatus[];
  current: StageId;
  notice: string;
}

/** `GET /development/skills`. */
export interface SkillsResponse {
  matrix: SkillMatrix;
  notice: string;
}

/** `GET /development/recommendations`. */
export interface RecommendationsResponse {
  recommendations: Recommendation[];
}

/** `GET /development/readiness-review`. */
export interface ReadinessReviewResponse {
  review: CareerReadinessReview;
}

export interface CountPair {
  done: number;
  total: number;
}

export interface ActiveLab {
  id: string;
  labId: string;
  mode: string;
  startedAt: string;
}

export interface ActiveProject {
  id: string;
  projectId: string;
  careerId: string;
  mode: string;
  status: string;
  startedAt: string;
}

export interface ActiveScenario {
  id: string;
  scenarioId: string;
  careerId: string;
  phase: string;
  startedAt: string;
}

export interface WeakArea {
  skillId: string;
  name: string;
  status: SkillMasteryStatus;
  mastery: number;
  recommendedNext: { kind: string; id: string; title: string; reason: string } | null;
}

export interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

/** Days the learner recorded activity on. Neutral by design: a gap costs nothing. */
export interface StreakSummary {
  current: number;
  longest: number;
  lastDay?: string;
}

/** `GET /development/overview`: everything the dashboard command center reads. */
export interface DevelopmentOverview {
  primaryCareer: { id: string; name: string; family?: string };
  enrollments: Enrollment[];
  stage: StageStatus | null;
  progress: { lessons: CountPair; labs: CountPair; scenarios: CountPair; projects: CountPair };
  skillCounts: Record<SkillMasteryStatus, number>;
  streak: StreakSummary;
  nextAction: Recommendation | null;
  activeLabs: ActiveLab[];
  activeProjects: ActiveProject[];
  activeScenarios: ActiveScenario[];
  certificationPrep: CertificationReadiness[];
  portfolioCount: number;
  weakAreas: WeakArea[];
  dueReviews: number;
  resumeChecklist: ChecklistItem[];
  notice: string;
}

export interface OverviewResponse {
  overview: DevelopmentOverview;
}

export interface EnrollmentPatch {
  status?: Enrollment['status'];
  primary?: boolean;
}

const enc = encodeURIComponent;
const q = (career?: string, extra?: Record<string, string | number | undefined>): string => {
  const params = new URLSearchParams();
  if (career) params.set('career', career);
  for (const [k, v] of Object.entries(extra ?? {})) if (v !== undefined) params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
};

export const development = {
  /** The learner's tracks. The profile's selected career is enrolled lazily as the primary goal. */
  enrollments: () => request<EnrollmentsResponse>('GET', '/development/enrollments'),
  /** Enroll in an available career. Omitting `primary` makes the first enrollment primary. */
  enroll: (careerId: string, primary?: boolean) => request<EnrollmentMutation>('POST', '/development/enrollments', primary === undefined ? { careerId } : { careerId, primary }),
  /** Pause, resume, complete or promote a track. Clearing the primary flag is refused by the server. */
  updateEnrollment: (careerId: string, patch: EnrollmentPatch) => request<EnrollmentMutation>('PATCH', `/development/enrollments/${enc(careerId)}`, patch),
  /** The eight stages with the requirements each one counts. */
  stages: (career?: string) => request<StagesResponse>('GET', `/development/stages${q(career)}`),
  /** The skill matrix for one track: status, mastery, evidence and the recommended next step. */
  skills: (career?: string) => request<SkillsResponse>('GET', `/development/skills${q(career)}`),
  /** Ranked recommendations, each with a reason. `minutes` drops anything longer. */
  recommendations: (career?: string, minutes?: number) => request<RecommendationsResponse>('GET', `/development/recommendations${q(career, { minutes })}`),
  /** The career readiness review: checks, gaps and strengths. Never a statement about employability. */
  readinessReview: (career?: string) => request<ReadinessReviewResponse>('GET', `/development/readiness-review${q(career)}`),
  /** The dashboard command center. 400 when no track has been chosen yet. */
  overview: () => request<OverviewResponse>('GET', '/development/overview'),
};
